import { Trans } from "@lingui/macro";
import { useLocalStorageValue } from "@react-hookz/web";
import { ApiEpPageconfig, IApiSmpGptAgent } from "@smp/api";
import { UiButton, UiSpinner, UiTypography } from "@smp/ui";
import { FC, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMount } from "react-use";
import { v4 as uuidv4 } from "uuid";

import { useCustomLocalStorage } from "@/hooks/use_custom_local_storage";
import { EPromptKey } from "@/hooks/use_prompt_fetcher";
import { Disclaimer } from "@/screens/app/components/dialogs/dialog_advisor/components/disclaimer";
import { useGptAsk } from "@/screens/app/components/dialogs/dialog_advisor/hooks/use_gpt_ask";
import { useContextApp } from "@/screens/app/context/app";
import {
  STORAGE_KEY__INSTA_VIEW_GROUP,
  STORAGE_KEY__WEEK_VIEW_GROUP,
} from "@/screens/app/screens/new_posts/context";

import { Stopwatch } from "./components/stopwatch";
import classes from "./posts_generator.module.css";

export interface IPostsGeneratorProps {
  agent: IApiSmpGptAgent;
  onSuccess?: () => void;
}

export const PostsGenerator: FC<IPostsGeneratorProps> = ({
  agent,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [, setStorageWeekSocialPageGroupId] = useLocalStorageValue<number>(
    STORAGE_KEY__WEEK_VIEW_GROUP,
    0,
  );
  const [, setStorageInstaSocialPageGroupId] = useLocalStorageValue<number>(
    STORAGE_KEY__INSTA_VIEW_GROUP,
    0,
  );

  const {
    state: { me },
  } = useContextApp();

  const [advisorValue, setAdvisorValue] =
    useCustomLocalStorage("ep.advisor_v2");

  const [hasError, setHasError] = useState(false);

  const gptAsk = useGptAsk();

  const process = useCallback(async () => {
    try {
      const arr = await gptAsk<
        Array<{
          title: string;
          text: string;
          unsplash: string;
          hashtags: string;
        }>
      >(
        EPromptKey.ADVISOR_ASK_FINAL_PROMPT,
        {
          num: agent.params?.post?.num,
          language: agent.params?.post?.language,
          topic: agent.params?.post?.topic,
        },
        {
          agent: agent.id,
        },
      );
      const socialPageGroupId = agent.params?.socialPageGroupId;

      setAdvisorValue({
        entries: [
          ...(advisorValue?.entries || []),
          {
            userId: me.id,
            request: {
              agentId: agent.id,
              socialPageGroupId:
                typeof socialPageGroupId === "number"
                  ? socialPageGroupId
                  : undefined,
            },
            response: arr.map((item) => ({
              ...item,
              uid: uuidv4(),
            })),
          },
        ],
      });

      if (socialPageGroupId) {
        setStorageWeekSocialPageGroupId(socialPageGroupId);
        setStorageInstaSocialPageGroupId(socialPageGroupId);
      }

      const isRedirectToFeed =
        !socialPageGroupId ||
        (me?.settings.ui.post_list_variant === "instaview" &&
          !(
            await ApiEpPageconfig.list({
              query: {
                all_organization_contexts: "True",
                medium__in: ["instagram", "instagram_business"],
                group: socialPageGroupId,
              },
            })
          )?.results.length) ||
        me?.settings.ui.post_list_variant === "list";

      navigate(isRedirectToFeed ? "/new_posts/feed" : "/new_posts");

      onSuccess?.();
    } catch (e) {
      setHasError(true);
    }
  }, [
    advisorValue?.entries,
    agent.id,
    agent.params?.post?.language,
    agent.params?.post?.num,
    agent.params?.post?.topic,
    agent.params?.socialPageGroupId,
    gptAsk,
    me.id,
    me?.settings.ui.post_list_variant,
    navigate,
    onSuccess,
    setAdvisorValue,
    setStorageInstaSocialPageGroupId,
    setStorageWeekSocialPageGroupId,
  ]);

  const handleRetry = useCallback(() => {
    setHasError(false);
    process();
  }, [process]);

  useMount(process);

  if (hasError) {
    return (
      <div className={classes.error}>
        <UiTypography color="error" variant="h5">
          <Trans>Something went wrong... Try your request again later</Trans>
        </UiTypography>

        <UiButton variant="outlined" size="large" onClick={handleRetry}>
          <Trans>Repeat request</Trans>
        </UiButton>
      </div>
    );
  }

  return (
    <>
      <div className={classes.loader}>
        <UiSpinner size={32} />

        <UiTypography className={classes.loader__text}>
          <Trans>AI SMM assistant creates posts...</Trans>
        </UiTypography>

        <div className={classes.loader__stopwatch}>
          <Stopwatch />
        </div>
      </div>

      <Disclaimer mt={3} />
    </>
  );
};
